import axios from 'axios';
import to from 'await-to-js';
import Vue from 'vue';

import {
  find,
  isEmpty,
  findIndex,
  sortBy,
  last,
  cloneDeep,
  filter,
  get,
  keyBy,
  uniqBy,
} from 'lodash';
import moment from 'moment';
import { jobStatuses } from '@enums/jobapi';
import downloadXlsxWorkpackage from '../utils/download-xlsx-workpackage';
import { getExportFileName } from '@sharedModules/data/utils/export-utils';
import sortStoreGroups from '../../utils/sort-store-groups-util';
import downloadCsvUtils from '../utils/download-csv';
import dateHasPassed from '../utils/date-checking';
import workpackageTypes from '@enums/workpackage-types';
import {
  yearMonthDayFormat,
  yearMonthDayHoursMinutesFormat,
  yearMonthDayHoursMinutesFormatWithColon,
} from '@enums/date-formats';
import getWorkPackageIdFromRoute from '../utils/workpackage-id-from-route';
import { XSR_COLUMNS } from '@sharedModules/data/enums/xsr-export-fields';
import { exportToSAP } from '@sharedModules/data/enums/export-type';

import {
  enableAOExtracts,
  usePeriodIdsInAOExtracts,
  createWorkpackageReadsFromDatabricks,
  enableCompetitorSimulation,
  enableRecalculateAllKPIs,
  enableCalculatedCompletionDate,
  calculatedCompletionDateDaysBeforeGoLive,
  enableSkipOptimisation,
  enableWorkpackagePeriodTypes,
  afterCreateWorkpackageUseBullQueue,
} from '@enums/feature-flags';

const getInitialWorkpackageSpecificState = () => {
  return {
    workpackageTruncationLength: 53,
    selectedWorkpackage: {},
    selectedWorkpackageToolStoreGroups: [],
    loadingSelectedWorkpackageToolStoreGroups: false,
    selectedUnitLevelEntryKey: null,
    loading: false,
    inactiveLoading: false,
    exportingIds: [],
    postingIds: [],
    exportingToSAPIds: [],
    refreshingIds: [],
    deletingIds: [],
    workpackagesInCreation: [],
    /**
     * @property {WorkpackageTransferPair[]} workpackagesInTransfer - Pairs of workpackages involved in a transfer operation.
     * @typedef {Object} WorkpackageTransferPair
     * @property {string} dest - The destination workpackage ID.
     * @property {string} src - The source workpackage ID.
     */
    workpackagesInTransfer: [],
    creating: false,
    numberOfPriceChangesByWpIdAndCategoryId: {},
    userWorkpackageStoreGroup: {},
    masterWorkpackageStoreGroup: {},
    productsGoLiveBeforeToday: [],
    productsGoLiveBeforeWorkpackage: [],
    productsGoLiveAfterWorkpackage: [],
    aoExtracts: [],
    periodIds: [],
    createWorkpackageRefreshFromFeed: false,
    createWorkpackageOptimisePrices: true,
    rerunningPriceValidations: false,
  };
};
const store = {
  namespaced: true,

  state: {
    ...getInitialWorkpackageSpecificState(),
    workpackages: [],
  },

  getters: {
    masterWorkpackages: state =>
      state.workpackages.filter(({ type }) => type === workpackageTypes.master),

    isMasterWorkpackage: state =>
      get(state.selectedWorkpackage, 'type') === workpackageTypes.master,

    activeWorkpackages: state => {
      return state.workpackages.filter(
        ({ goLiveDate, type, description }) =>
          goLiveDate !== null &&
          !dateHasPassed(goLiveDate) &&
          type !== workpackageTypes.master &&
          !find(state.workpackagesInCreation, { description, goLiveDate })
      );
    },

    mockedActiveWorkpackages: state => {
      return state.workpackagesInCreation.filter(
        ({ goLiveDate }) => goLiveDate !== null && !dateHasPassed(goLiveDate)
      );
    },

    // note this is only populated when archived workpackages are fetched, e.g. archived section is expanded.
    archivedWorkpackages: state => {
      return state.workpackages.filter(
        ({ goLiveDate, type, description }) =>
          goLiveDate !== null &&
          dateHasPassed(goLiveDate) &&
          type !== workpackageTypes.master &&
          !find(state.workpackagesInCreation, { description, goLiveDate })
      );
    },

    mockedArchivedWorkpackages: state => {
      return state.workpackagesInCreation.filter(
        ({ goLiveDate }) => goLiveDate !== null && dateHasPassed(goLiveDate)
      );
    },

    getSelectedWorkpackage: state => state.selectedWorkpackage,

    hasSelectedWorkpackage: state => !isEmpty(state.selectedWorkpackage),

    hasAOExtracts: (state, getters, rootState) =>
      rootState.clientConfig.toggleLogic[enableAOExtracts] &&
      !isEmpty(get(state.selectedWorkpackage, 'aoExtracts')),

    isSelectedWorkpackageMaster: state =>
      state.selectedWorkpackage && state.selectedWorkpackage.type === workpackageTypes.master,

    getToolStoreGroupPairs: state => {
      const { selectedWorkpackageToolStoreGroups } = state;
      return selectedWorkpackageToolStoreGroups.reduce((prev, curr) => {
        return {
          ...prev,
          [curr.description]: curr.key,
        };
      }, {});
    },
    // Returns true if any provided workpackage ID is in the transfer list
    isAnyWorkpackageInTransferList: state => workpackageIds => {
      return state.workpackagesInTransfer.some(
        wpPair => workpackageIds.includes(wpPair.dest) || workpackageIds.includes(wpPair.src)
      );
    },

    getToolStoreGroupValues: state => {
      // Fake back-end response and return an array of all possible toolStoreGroup values
      const { selectedWorkpackageToolStoreGroups } = state;
      return selectedWorkpackageToolStoreGroups.map(relationship => {
        return {
          attributeKey: relationship.description,
          attributeValue: relationship.description,
        };
      });
    },

    getAOExtracts: state =>
      state.aoExtracts.map(extract => ({
        ...extract,
        displayName: `${extract.extractName} ${moment(extract.auditCreatedTime).format(
          yearMonthDayHoursMinutesFormatWithColon
        )}`,
      })),

    getAOExtractsByKey: (state, getters) => {
      return keyBy(getters.getAOExtracts, 'auditCorrelationId');
    },

    getAvailableAOExtracts: (state, getters, rootState) => goLiveDate =>
      rootState.clientConfig.toggleLogic[usePeriodIdsInAOExtracts]
        ? getters.getAOExtracts.filter(extract =>
            extract.periodIds.some(
              periodId => !!getters.getPeriodIdsByGoLiveDateByKey(goLiveDate)[periodId]
            )
          )
        : getters.getAOExtracts,

    getPeriodIdsByKey: state => keyBy(state.periodIds, 'periodId'),

    getAssociatedPeriodIds: (state, getters) => selectedAOExtracts => {
      if (isEmpty(getters.getAOExtracts)) {
        return '';
      }
      const periodsIds = new Set();
      selectedAOExtracts.forEach(id => {
        getters.getAOExtractsByKey[id].periodIds.map(periodId => periodsIds.add(periodId));
      });
      return Array.from(periodsIds).join(', ');
    },

    isAOExtractsValid: (state, getters, rootState) => selectedAOExtracts => {
      if (
        !rootState.clientConfig.toggleLogic[enableAOExtracts] ||
        !rootState.clientConfig.toggleLogic[usePeriodIdsInAOExtracts] ||
        isEmpty(selectedAOExtracts) ||
        selectedAOExtracts.length === 1
      ) {
        return true;
      }

      // if more than 1 extract selected - check that associated periodIds have the same dates
      let effectiveDate;
      let expiryDate;
      return !selectedAOExtracts.some(id => {
        return getters.getAOExtractsByKey[id].periodIds.some(periodId => {
          if (!effectiveDate) {
            effectiveDate = getters.getPeriodIdsByKey[periodId].effectiveDate;
            expiryDate = getters.getPeriodIdsByKey[periodId].expiryDate;
            return false;
          }
          return (
            getters.getPeriodIdsByKey[periodId].effectiveDate !== effectiveDate ||
            getters.getPeriodIdsByKey[periodId].expiryDate !== expiryDate
          );
        });
      });
    },

    getPeriodIdsByGoLiveDateByKey: state => goLiveDate => {
      const goLiveDateUtc = moment.utc(goLiveDate).toISOString();
      return keyBy(
        state.periodIds.filter(
          periodId => periodId.effectiveDate <= goLiveDateUtc && goLiveDateUtc < periodId.expiryDate
        ),
        'periodId'
      );
    },

    createWorkpackageWorkflowName: (state, getters, rootState) => {
      return (
        rootState.clientConfig.toggleLogic.createWorkpackageWorkflow || 'run_create_workpackage'
      );
    },

    calculateCompletionDate: (state, getters, rootState) => ({ goLiveDate }) => {
      if (rootState.clientConfig.toggleLogic[enableCalculatedCompletionDate] !== true) {
        return;
      }

      const now = moment();
      const daysBeforeGoLive =
        rootState.clientConfig.toggleLogic[calculatedCompletionDateDaysBeforeGoLive] || 0;
      const calculatedCompletionDate = moment(goLiveDate).subtract(daysBeforeGoLive, 'days');
      // if calculatedCompletionDate would be in the past, return today's date instead, otherwise return the calculatedCompletionDate
      return moment(Math.max(now, calculatedCompletionDate)).format('YYYY-MM-DD');
    },
  },

  mutations: {
    setWorkpackages(state, workpackages) {
      state.workpackages = workpackages;
    },

    setSelectedWorkpackageToolStoreGroups(state, toolStoreGroups) {
      state.selectedWorkpackageToolStoreGroups = toolStoreGroups;
    },

    setLoadingSelectedWorkpackageToolStoreGroups(state, loadingSelectedWorkpackageToolStoreGroups) {
      state.loadingSelectedWorkpackageToolStoreGroups = loadingSelectedWorkpackageToolStoreGroups;
    },

    setInactiveWorkpackages(state, workpackages) {
      const currentWps = cloneDeep(state.workpackages);
      // if this is called multiple times we can end up with duplicates.
      // ensure unique by _id, keeping values from workpackages param over existing state.
      state.workpackages = uniqBy([...workpackages, ...currentWps], '_id');
    },

    setLoading(state, isLoading) {
      state.loading = isLoading;
    },

    setInactiveLoading(state, isLoading) {
      state.inactiveLoading = isLoading;
    },

    setCreatingStatus(state, isLoading) {
      state.creating = isLoading;
    },

    addWorkpackageToDeletingList(state, workpackageId) {
      state.deletingIds = [...state.deletingIds, workpackageId];
    },

    removeWorkpackageFromDeletingList(state, workpackageId) {
      state.deletingIds = state.deletingIds.filter(id => id !== workpackageId);
    },

    addWorkpackageToExportingList(state, workpackageId) {
      state.exportingIds = [...state.exportingIds, workpackageId];
    },

    addWorkpackageToPostingList(state, workpackageId) {
      state.postingIds = [...state.postingIds, workpackageId];
    },

    addWorkpackageToExportingToSAPList(state, workpackageId) {
      state.exportingToSAPIds = [...state.exportingToSAPIds, workpackageId];
    },

    addWorkpackageToCreationList(state, mockWorkpackage) {
      state.workpackagesInCreation = [...state.workpackagesInCreation, mockWorkpackage];
    },

    setWorkpackageToCreationList(state, mockWorkpackages) {
      state.workpackagesInCreation = mockWorkpackages;
    },

    addWorkpackagesToTransferList(state, wps) {
      state.workpackagesInTransfer = [...state.workpackagesInTransfer, { ...wps }];
    },

    /**
     * Remove workpackages from the transfer list by destination workpackage ID.
     * @param {Object} state - The Vuex state object.
     * @param {string} dest - The destination workpackage ID.
     * @returns {void}
     */
    removeWorkpackagesFromToTransferList(state, { dest }) {
      // using only destination Id to find item to remove as source Id is not always available
      // and we can't have multiple transfers to the same destination
      state.workpackagesInTransfer = state.workpackagesInTransfer.filter(
        wpPair => wpPair.dest !== dest
      );
    },

    setWorkpackagesInTransferList(state, wps) {
      state.workpackagesInTransfer = wps;
    },

    removeWorkpackageFromExportingList(state, workpackageId) {
      state.exportingIds = state.exportingIds.filter(id => id !== workpackageId);
    },

    removeWorkpackageFromPostingList(state, workpackageId) {
      state.postingIds = state.postingIds.filter(id => id !== workpackageId);
    },

    removeWorkpackageFromExportingToSAPList(state, workpackageId) {
      state.exportingToSAPIds = state.exportingToSAPIds.filter(id => id !== workpackageId);
    },

    addWorkpackageToRefreshingList(state, workpackageId) {
      state.refreshingIds = [...state.refreshingIds, workpackageId];
    },

    removeWorkpackageFromRefreshingList(state, workpackageId) {
      state.refreshingIds = state.refreshingIds.filter(id => id !== workpackageId);
    },

    setWorkpackagesRefreshingList(state, workpackageIds) {
      state.refreshingIds = workpackageIds;
    },

    removeWorkpackageFromCreationList(state, { descriptions }) {
      const filteredCreationList = state.workpackagesInCreation.filter(
        wp => !descriptions.includes(wp.description)
      );
      state.workpackagesInCreation = filteredCreationList;
    },

    setSelectedWorkpackage(state, workpackage) {
      state.selectedWorkpackage = workpackage;
    },

    setSelectedUnitLevelEntryKey(state, unitLevelEntryKey) {
      state.selectedUnitLevelEntryKey = unitLevelEntryKey;
    },

    setNumberOfPriceChangesByWpIdAndCategoryId(state, priceChanges) {
      state.numberOfPriceChangesByWpIdAndCategoryId = {
        ...state.numberOfPriceChangesByWpIdAndCategoryId,
        ...priceChanges,
      };
    },

    resetWorkpackageSpecificState(state) {
      Object.assign(state, getInitialWorkpackageSpecificState());
    },

    updateWorkpackageMetaData(state, updatedWorkpackage) {
      const index = findIndex(state.workpackages, { _id: updatedWorkpackage._id });
      Vue.set(state.workpackages[index], 'lastUpdated', updatedWorkpackage.lastUpdated);
      Vue.set(
        state.workpackages[index],
        'competitorLastUpdated',
        updatedWorkpackage.competitorLastUpdated
      );
    },

    setUserWorkPackageStoreGroup(state, wpsg) {
      state.userWorkpackageStoreGroup = wpsg;
    },

    setMasterWorkPackageStoreGroup(state, wpsg) {
      state.masterWorkpackageStoreGroup = wpsg;
    },

    setProductsGoLiveBeforeToday(state, productsGoLiveBeforeToday) {
      state.productsGoLiveBeforeToday = productsGoLiveBeforeToday;
    },

    setProductsGoLiveBeforeWorkpackage(state, productsGoLiveBeforeWorkpackage) {
      state.productsGoLiveBeforeWorkpackage = productsGoLiveBeforeWorkpackage;
    },

    setProductsGoLiveAfterWorkpackage(state, productsGoLiveAfterWorkpackage) {
      state.productsGoLiveAfterWorkpackage = productsGoLiveAfterWorkpackage;
    },

    setAOExtracts(state, aoExtracts) {
      state.aoExtracts = aoExtracts;
    },

    setPeriodIds(state, periodIds) {
      state.periodIds = periodIds;
    },

    setCreateWorkpackageRefreshFromFeed(state, value) {
      state.createWorkpackageRefreshFromFeed = value;
    },

    setCreateWorkpackageOptimisePrices(state, value) {
      state.createWorkpackageOptimisePrices = value;
      // if optimisation is turned off, we want to default to refreshing from feed to ensure correct intention price is set as scenario price.
      // If user turns optimisation back on they can choose whether to refresh from feed or not.
      if (!value) {
        state.createWorkpackageRefreshFromFeed = true;
      }
    },

    defaultCreateWorkpackageOptions(state) {
      state.createWorkpackageRefreshFromFeed = false;
      state.createWorkpackageOptimisePrices = true;
    },

    setExactCopyWorkpackageOptions(state) {
      state.createWorkpackageOptimisePrices = false;
      state.createWorkpackageRefreshFromFeed = false;
    },

    setRerunningPriceValidations(state, isRerunning) {
      state.rerunningPriceValidations = isRerunning;
    },
  },

  actions: {
    async fetchWorkpackages({ rootState, commit, dispatch }, { params = {} } = {}) {
      if (params.wpActive === false) {
        commit('setInactiveLoading', true);
      } else {
        commit('setLoading', true);
        await dispatch('getRunningWorkpackageJobs');
      }
      dispatch('getRunningTransferJobs');
      if (rootState.clientConfig.toggleLogic.refreshWorkpackageFromDatabricks) {
        dispatch('getRunningRefreshJobs');
      }
      const [err, { data: workpackages }] = await to(
        axios.get('/api/work-packages/units-aggregate', { params })
      );
      if (err) throw new Error(err.message);
      if (params.wpActive === false) {
        commit('setInactiveWorkpackages', workpackages);
        dispatch('reloadSelectedWorkpackage');
        commit('setInactiveLoading', false);
      } else {
        commit('setWorkpackages', workpackages);
        dispatch('reloadSelectedWorkpackage');
        commit('setLoading', false);
      }
      return workpackages.map(wp => wp._id);
    },

    /**
     * Fetch a single workpackage by workpackageId
     * If selectedWorkpackage matches workpackageId, update state
     * If state.workpackages contains workpackage with matching workpackageId, update state
     */
    async fetchWorkpackage({ state, commit }, { workpackageId }) {
      try {
        commit('setLoading', true);
        const { data: workpackage } = await axios.get(`/api/work-packages/${workpackageId}`);
        if (workpackageId === state.selectedWorkpackage._id) {
          commit('setSelectedWorkpackage', workpackage);
        }
        const workpackageIndex = findIndex(state.workpackages, { _id: workpackageId });
        if (workpackageIndex !== -1) {
          Vue.set(state.workpackages, workpackageIndex, workpackage);
        }
      } catch (error) {
        console.error('Error fetching workpackage:', error);
      } finally {
        commit('setLoading', false);
      }
    },

    // ideally selectedWorkpackage would be computed via getter with only selectedWorkpackageId stored in state,
    // but because we have state.selectedWorkpackage it can get out of sync with the state.workpackages array.
    // this action finds workpackage by id as reassigns selectedWorkpackage.
    reloadSelectedWorkpackage({ state, commit }) {
      const selectedWorkpackageId = get(state, 'selectedWorkpackage._id');
      if (isEmpty(selectedWorkpackageId)) return;

      const reloadedWorkpackage = find(state.workpackages, { _id: selectedWorkpackageId });
      if (!isEmpty(reloadedWorkpackage))
        return commit('setSelectedWorkpackage', reloadedWorkpackage);
    },

    async refreshWorkpackage({ commit, rootState }, params = {}) {
      // return true if successful and false if not
      const { workpackageId } = params;
      commit('addWorkpackageToRefreshingList', workpackageId);
      const payload = {
        workpackage_id: workpackageId,
        trigger_kpis: !!rootState.clientConfig.toggleLogic[enableRecalculateAllKPIs],
        jobApiTask: 'run_refresh_workpackage',
      };

      const [err] = await to(
        axios.post(`/api/work-packages/workpackage/${workpackageId}/refresh-workpackage`, {
          params: payload,
        })
      );
      if (err) {
        commit('removeWorkpackageFromRefreshingList', workpackageId);
      }
      return err === null;
    },

    async afterRefreshWorkpackage({ commit, dispatch, getters }, { id }) {
      commit('removeWorkpackageFromRefreshingList', id);
      const isArchivedWorkpackage = find(getters.archivedWorkpackages, { _id: id });
      const promises = [
        dispatch('notifications/resetProgressNotifications', {}, { root: true }),
        isArchivedWorkpackage
          ? dispatch('fetchWorkpackages', { params: { wpActive: false } })
          : dispatch('fetchWorkpackages', { params: { wpActive: true } }),
      ];
      await Promise.all(promises);
    },

    async afterFailedRefreshWorkpackage({ commit, dispatch }, { id }) {
      commit('removeWorkpackageFromRefreshingList', id);
      await dispatch('notifications/resetProgressNotifications', {}, { root: true });
    },

    // Note: Argo does not update the job status immediately; it takes a few seconds to reflect the current state.
    // Therefore, if you query the status immediately after transferring a work package, it may show the job as running, even if it has completed.
    async getRunningRefreshJobs({ commit }) {
      const [err, res] = await to(axios.get('/api/work-packages/refresh-jobs'));
      if (err) {
        throw new Error(err.message);
      }
      const { data: refreshJobs } = res;
      const workpackageIdsBeingRefreshed = refreshJobs.map(job => job.params.workpackage_id);

      commit('setWorkpackagesRefreshingList', workpackageIdsBeingRefreshed);
    },

    async createWorkpackage({ commit, dispatch, state, rootState, getters }, params = {}) {
      // return true if successful and false if not
      commit('setCreatingStatus', true);

      const sourceWorkpackage = state.workpackages.find(
        workpackage => workpackage._id === params.workpackageId
      );
      const mockWorkpackage = {
        ...params,
        mocked: true,
        isCloneJob: false,
        sourceWorkpackageDescription:
          sourceWorkpackage !== undefined ? sourceWorkpackage.description : undefined,
      };
      const payload = {
        store_format: params.storeFormat,
        hierarchy_ids: params.hierarchyIds,
        hierarchy_level: params.hierarchyLevel,
        go_live_date: params.goLiveDate,
        completion_date: params.completionDate,
        reference_date: get(params, 'referenceDate'),
        owner: params.owner,
        description: params.description,
        is_copy: params.isCopy || false,
        jobApiTask: getters.createWorkpackageWorkflowName,
      };

      if (rootState.clientConfig.toggleLogic[enableAOExtracts]) {
        payload.ao_extracts = params.aoExtracts;
      }

      if (rootState.clientConfig.toggleLogic[enableCompetitorSimulation]) {
        payload.cost_thresholds = get(params, 'costChangeThresholds');
      }

      if (rootState.clientConfig.toggleLogic[createWorkpackageReadsFromDatabricks]) {
        payload.refresh_from_feed = state.createWorkpackageRefreshFromFeed;
      }

      if (rootState.clientConfig.toggleLogic[enableSkipOptimisation]) {
        payload.skip_optimisation = !state.createWorkpackageOptimisePrices;
      }

      if (rootState.clientConfig.toggleLogic[enableWorkpackagePeriodTypes]) {
        payload.workpackage_period_type = get(params, 'workpackagePeriodType');
      }

      payload.use_bullqueue = get(
        rootState.clientConfig.toggleLogic,
        afterCreateWorkpackageUseBullQueue,
        true
      );

      const [err] = await to(
        axios.post(`/api/work-packages/workpackage/${params.workpackageId}`, { params: payload })
      );
      commit('setCreatingStatus', false);
      if (err) {
        dispatch('addFailedCreateWorkpackageNotification', { description: params.description });
        return false;
      }
      // Only create mock workpackage place holder if successfully triggered
      commit('addWorkpackageToCreationList', mockWorkpackage);
      return err === null;
    },

    async afterCloneWorkpackage(
      { commit, dispatch, getters, state },
      { payload: cloneDescription }
    ) {
      commit('removeWorkpackageFromCreationList', { descriptions: [cloneDescription] });

      const archivedWpsExist = !isEmpty(getters.archivedWorkpackages);
      const promises = [
        dispatch('notifications/resetAllNotifications', {}, { root: true }),
        dispatch('fetchWorkpackages', { params: { wpActive: true } }),
      ];
      await Promise.all(promises);
      if (archivedWpsExist) {
        await dispatch('fetchWorkpackages', { params: { wpActive: false } });
      }
      const clonedWp = last(
        sortBy(filter(state.workpackages, wp => cloneDescription === wp.description), wp =>
          moment.min(moment(wp.lastUpdated))
        )
      );
      if (clonedWp) {
        await dispatch(
          'hierarchy/fetchHierarchyForWorkpackage',
          { params: { where: { workpackageId: clonedWp._id, wholesale: false } } },
          { root: true }
        );
      }
    },

    async afterCreateWorkpackage(
      { commit, dispatch, state, getters },
      { payload: newWpDescription }
    ) {
      commit('removeWorkpackageFromCreationList', { descriptions: [newWpDescription] });
      const archivedWpsExist = !isEmpty(getters.archivedWorkpackages);
      const promises = [
        dispatch('notifications/resetAllNotifications', {}, { root: true }),
        dispatch('fetchWorkpackages', { params: { wpActive: true } }),
      ];
      await Promise.all(promises);
      if (archivedWpsExist) {
        // setInactiveWorkpackages depends on setWorkpackages already running, so state.workpackages must already have results for cloneDeep
        await dispatch('fetchWorkpackages', { params: { wpActive: false } });
      }
      const latestWp = last(
        sortBy(
          filter(
            state.workpackages,
            wp => wp.type === workpackageTypes.workpackage && newWpDescription === wp.description
          ),
          wp => moment.min(moment(wp.lastUpdated))
        )
      );
      await dispatch(
        'hierarchy/fetchHierarchyForWorkpackage',
        { params: { where: { workpackageId: latestWp._id, wholesale: false } } },
        { root: true }
      );
    },

    async afterCreateWorkpackageDag({ commit, dispatch }, { payload: newWpDescription }) {
      await dispatch('afterCreateWorkpackage', { payload: newWpDescription });
      // need to explicitly create notification handled by bullqueue for run_create_workpackage
      // must run after resetAllNotifications, otherwise notification will be lost
      const jobId = 'run_pricing_for_all';
      const jobStatus = jobStatuses.sentToJobApiFromCreation;
      commit(
        'notifications/addNotification',
        {
          id: `${jobId}:${jobStatus}:${new Date().toISOString()}`,
          jobStatus,
          newWpDescription,
          baseTranslation: `notifications.${jobId}`,
        },
        { root: true }
      );
    },

    async afterFailedCreateWorkpackage({ commit, dispatch }, { payload: newWpDescription }) {
      commit('removeWorkpackageFromCreationList', { descriptions: [newWpDescription] });
      await dispatch('notifications/resetAllNotifications', {}, { root: true });
    },

    async transferWorkpackage(
      { commit },
      { destinationWorkpackageId, sourceWorkpackageId, fields, selectedCategories }
    ) {
      const updates = fields.map(field => field.id);
      commit('addWorkpackagesToTransferList', {
        dest: destinationWorkpackageId,
        src: sourceWorkpackageId,
      });
      try {
        await axios.post(
          `/api/work-packages/workpackage/${destinationWorkpackageId}/transfer-workpackage`,
          {
            jobApiTask: 'transfer_workpackage',
            sourceWorkpackageId,
            updates,
            selectedCategories,
          }
        );
      } catch (err) {
        // let job api notifications handle removeWorkpackageFromTransferList if no error was thrown, use afterTransferWorkpackage.
        commit('removeWorkpackagesFromToTransferList', {
          dest: destinationWorkpackageId,
        });
        console.info(err);
      }
    },

    async validateDataBeforeTransferring(
      _,
      { destinationWorkpackageId, sourceWorkpackageId, categories, fields }
    ) {
      const [err, result] = await to(
        axios.get(
          `/api/work-packages/workpackage/${destinationWorkpackageId}/transfer-workpackage/${sourceWorkpackageId}/validation`,
          {
            params: { categories, fields },
          }
        )
      );
      return err ? {} : result.data;
    },

    // Note: Argo does not update the job status immediately; it takes a few seconds to reflect the current state.
    // Therefore, if you query the status immediately after creating a work package, it may show the job as running, even if it has completed.
    async getRunningWorkpackageJobs({ commit, getters }) {
      const [[createErr, createRes], [cloneErr, cloneRes]] = await Promise.all([
        to(
          axios.get('/api/work-packages/creationJobs', {
            params: { jobType: getters.createWorkpackageWorkflowName },
          })
        ),
        to(
          axios.get('/api/work-packages/creationJobs', {
            params: { jobType: 'run_clone_workpackage' },
          })
        ),
      ]);

      if (createErr && cloneErr) {
        throw new Error('Failed to fetch creation jobs');
      }

      const creationJobs = createErr ? [] : createRes.data;
      const cloneJobs = cloneErr ? [] : cloneRes.data;

      const creationJobsWithMockedFlag = creationJobs.map(job => {
        return {
          goLiveDate: job.params.go_live_date,
          description: job.params.description,
          categoryIds: job.params.category_ids,
          completionDate: job.params.completion_date,
          mocked: true,
          isCloneJob: false,
          owner: job.params.owner,
          storeFormat: job.params.store_format,
          workpackageId: job.params.workpackage_id,
        };
      });

      const cloneJobsWithMockedFlag = cloneJobs.map(job => {
        return {
          description: job.params.new_workpackage_description,
          goLiveDate: job.params.go_live_date,
          mocked: true,
          isCloneJob: true,
          owner: job.params.new_workpackage_owner,
        };
      });

      commit('setWorkpackageToCreationList', [
        ...creationJobsWithMockedFlag,
        ...cloneJobsWithMockedFlag,
      ]);
    },

    // Note: Argo does not update the job status immediately; it takes a few seconds to reflect the current state.
    // Therefore, if you query the status immediately after transferring a work package, it may show the job as running, even if it has completed.
    async getRunningTransferJobs({ commit }) {
      const [err, res] = await to(axios.get('/api/work-packages/transfer-jobs'));
      if (err) {
        throw new Error(err.message);
      }
      const { data: transferJobs } = res;
      const pairInvolvedInTransfer = transferJobs.reduce((arr, obj) => {
        arr.push({
          dest: obj.params.destination_workpackage_id,
          src: obj.params.source_workpackage_id,
        });
        return arr;
      }, []);
      commit('setWorkpackagesInTransferList', pairInvolvedInTransfer);
    },

    async afterTransferWorkpackage({ commit, dispatch }, { id: destinationWorkpackageId }) {
      await dispatch('fetchWorkpackageData', {
        id: destinationWorkpackageId,
      });
      // removing workpackages pairs from transfer by destId
      commit('removeWorkpackagesFromToTransferList', {
        dest: destinationWorkpackageId,
      });
    },

    async downloadPenaltyExportData(context, { workpackage }) {
      // NOTE: This export uses quite a lot of dynamic fields for competitors and alerts
      // As this feature is only intended for a single banner, the column name mapping is done on the server.
      // As a result, I didn't use the existing middleware.
      const { _id } = workpackage;

      const [err, response] = await to(
        axios.get(`/api/scenario-results/workpackage/${_id}/export-penalty-data`)
      );
      if (err) throw new Error(err.message);

      const dateString = moment().format(yearMonthDayHoursMinutesFormat);
      const fileName = `Penalty_Export_${dateString}.csv`;

      downloadCsvUtils.downloadCsvFile(response.data, fileName);
    },

    async fetchWorkpackageData({ commit, state }, { id }) {
      const [err, { data: updatedWorkpackageData }] = await to(
        axios.get(`/api/work-packages/${id}`)
      );
      if (err) throw new Error(err.message);
      // If the workpackage is already in the store, update it. Otherwise, add it
      if (state.workpackages.find(wp => wp._id === id)) {
        commit('updateWorkpackageMetaData', updatedWorkpackageData);
      } else {
        commit('setWorkpackages', [...state.workpackages, updatedWorkpackageData]);
      }
    },

    async deleteWorkpackage({ commit }, workpackage) {
      commit('addWorkpackageToDeletingList', workpackage._id);

      const payload = {
        description: workpackage.description,
        jobApiTask: 'run_delete_workpackage',
      };

      const [err] = await to(
        axios.delete(`/api/work-packages/workpackage/${workpackage._id}`, {
          data: { params: payload },
        })
      );
      // setDeletingStatus in afterDeleteWorkpackage if no error was thrown, so that duplicate delete jobs aren't triggered.
      if (err) commit('removeWorkpackageFromDeletingList', workpackage._id);

      return err === null; // return true if successful and false if not
    },

    async cloneWorkpackage({ commit, rootState }, { workpackage, description }) {
      // Always generate a description so FE mock, BE job, and notification payload all use the same name.
      // Mirrors the Python pattern: f"OW Clone of {description} {timestamp}"
      const cloneDescription =
        description ||
        `OW Clone of ${workpackage.description} ${moment().format('YYYY-MM-DD HH:mm:ss')}`;

      const payload = {
        jobApiTask: 'run_clone_workpackage',
        new_workpackage_owner: rootState.context.profile.username,
        source_workpackage_id: workpackage._id,
        new_workpackage_description: cloneDescription,
        go_live_date: workpackage.goLiveDate,
      };

      const [err] = await to(
        axios.post(`/api/work-packages/workpackage/${workpackage._id}/clone`, { params: payload })
      );

      if (err) {
        console.error('Failed to clone workpackage', workpackage._id, err.message);
        return false;
      }

      const mockWorkpackage = {
        description: cloneDescription,
        goLiveDate: workpackage.goLiveDate,
        completionDate: workpackage.completionDate,
        storeFormat: workpackage.storeFormat,
        owner: rootState.context.profile.username,
        mocked: true,
        isCloneJob: true,
        sourceWorkpackageDescription: workpackage.description,
      };
      commit('addWorkpackageToCreationList', mockWorkpackage);

      return true;
    },

    async afterDeleteWorkpackage({ commit, dispatch, getters }, { id }) {
      commit('removeWorkpackageFromDeletingList', id);
      const archivedWpsExist = !isEmpty(getters.archivedWorkpackages);
      await dispatch('fetchWorkpackages', { params: { wpActive: true } });
      if (archivedWpsExist) dispatch('fetchWorkpackages', { params: { wpActive: false } });
    },

    async updateWorkpackage({ commit, dispatch }, { workpackage, updates }) {
      const jobId = 'updateWorkpackage';
      const notificationPayload = workpackage.description;
      try {
        commit('setLoading', true);
        const update = await axios.patch(`/api/work-packages/${workpackage._id}`, updates);
        // fetch single workpackage and update relevant state
        await dispatch('fetchWorkpackage', { workpackageId: workpackage._id });

        const jobStatus = jobStatuses.finished;
        // handle case where nothing changed. i.e. clicked v-text-field and moved away but made no changes
        if (get(update, 'data.modifiedCount') > 0) {
          commit(
            'notifications/addNotification',
            {
              id: `${jobId}:${jobStatus}:${new Date().toISOString()}`,
              jobStatus,
              notificationPayload,
              baseTranslation: `notifications.${jobId}`,
            },
            { root: true }
          );
        }
      } catch (err) {
        const jobStatus = jobStatuses.failed;
        commit(
          'notifications/addNotification',
          {
            id: `${jobId}:${jobStatus}:${new Date().toISOString()}`,
            jobStatus,
            notificationPayload,
            baseTranslation: `notifications.${jobId}`,
          },
          { root: true }
        );
      } finally {
        commit('setLoading', false);
      }
    },

    setWorkpackages({ commit }, workpackages) {
      commit('setWorkpackages', workpackages);
    },

    addExportWorkpackageNotification({ commit }, { selectedWorkpackage, jobStatus }) {
      const jobId = 'export_workpackage';
      const baseTranslation = `notifications.${jobId}`;
      const notification = {
        id: `${jobId}:${jobStatus}:${Date.now()}`,
        jobStatus,
        notificationPayload: selectedWorkpackage.description,
        baseTranslation,
      };
      commit('notifications/addNotification', notification, { root: true });
    },

    addPostWorkpackageNotification({ commit }, { selectedWorkpackage, jobStatus }) {
      const jobId = 'post_workpackage';
      const baseTranslation = `notifications.${jobId}`;
      const notification = {
        id: `${jobId}:${jobStatus}:${Date.now()}`,
        jobStatus,
        notificationPayload: selectedWorkpackage.description,
        baseTranslation,
      };
      commit('notifications/addNotification', notification, { root: true });
    },

    addFailedCreateWorkpackageNotification({ commit }, { description }) {
      const jobId = 'run_create_workpackage';
      const baseTranslation = `notifications.${jobId}`;
      const jobStatus = 'failed_to_send';
      const notification = {
        id: `${jobId}:${jobStatus}:${Date.now()}`,
        jobStatus,
        notificationPayload: description,
        baseTranslation,
      };
      commit('notifications/addNotification', notification, { root: true });
    },

    async previewWorkpackageResults({ commit }, { selectedWorkpackage, fields, exportType }) {
      if (!selectedWorkpackage) {
        return Promise.resolve([]);
      }
      commit('addWorkpackageToExportingList', selectedWorkpackage._id);
      const params = { export: true, fields, exportType };

      const [err, response] = await to(
        axios.get(`/api/work-packages/workpackage/${selectedWorkpackage._id}/preview-results`, {
          params,
        })
      );
      commit('removeWorkpackageFromExportingList', selectedWorkpackage._id);
      if (err) {
        throw new Error(err.message);
      }
      commit('setProductsGoLiveBeforeToday', get(response, 'data.productsGoLiveBeforeToday', []));
      commit(
        'setProductsGoLiveBeforeWorkpackage',
        get(response, 'data.productsGoLiveBeforeWP', [])
      );
      commit('setProductsGoLiveAfterWorkpackage', get(response, 'data.productsGoLiveAfterWP', []));
      commit('removeWorkpackageFromExportingList', selectedWorkpackage._id);
    },

    async downloadWorkpackageResults(
      { commit, dispatch },
      { selectedWorkpackage, fields, exportType, columnFormatters = {} }
    ) {
      if (!selectedWorkpackage) {
        return Promise.resolve([]);
      }
      dispatch('addExportWorkpackageNotification', {
        selectedWorkpackage,
        jobStatus: jobStatuses.starting,
      });
      commit('addWorkpackageToExportingList', selectedWorkpackage._id);
      const params = { export: true, fields, exportType };
      const { storeFormat } = selectedWorkpackage;
      const executionDate = moment(selectedWorkpackage.goLiveDate).format(yearMonthDayFormat);
      const exportTime = moment().format(yearMonthDayHoursMinutesFormat);
      const [err, response] = await to(
        axios.get(`/api/work-packages/workpackage/${selectedWorkpackage._id}/results`, { params })
      );
      if (err) {
        dispatch('addExportWorkpackageNotification', { selectedWorkpackage, jobStatus: 'failed' });
        commit('removeWorkpackageFromExportingList', selectedWorkpackage._id);
        throw new Error(err.message);
      }
      // response.data may be only header row. 2 or more rows mean at least one data row in export.
      if (isEmpty(response.data) || `${response.data}`.split('\n').length < 2) {
        dispatch('addExportWorkpackageNotification', {
          selectedWorkpackage,
          jobStatus: 'no_results',
        });
        commit('removeWorkpackageFromExportingList', selectedWorkpackage._id);
        return;
      }
      const filename = `${storeFormat} ${executionDate} - exported ${exportTime}.xlsx`;
      downloadXlsxWorkpackage(response.data, filename, { columnFormatters });
      commit('removeWorkpackageFromExportingList', selectedWorkpackage._id);
      dispatch('addExportWorkpackageNotification', {
        selectedWorkpackage,
        jobStatus: jobStatuses.finished,
      });
    },

    async exportWorkpackageResultsToAPI({ commit, dispatch }, { selectedWorkpackage, fields }) {
      if (!selectedWorkpackage) {
        return Promise.resolve([]);
      }
      dispatch('addPostWorkpackageNotification', {
        selectedWorkpackage,
        jobStatus: jobStatuses.starting,
      });
      commit('addWorkpackageToPostingList', selectedWorkpackage._id);
      const params = { export: true, fields };
      const [err, response] = await to(
        axios.post(`/api/work-packages/workpackage/${selectedWorkpackage._id}/results-to-api`, {
          params,
        })
      );
      if (err) {
        dispatch('addPostWorkpackageNotification', { selectedWorkpackage, jobStatus: 'failed' });
        commit('removeWorkpackageFromPostingList', selectedWorkpackage._id);
        throw new Error(err.message);
      }
      if (response.status === 204) {
        dispatch('addPostWorkpackageNotification', {
          selectedWorkpackage,
          jobStatus: 'no_results',
        });
        commit('removeWorkpackageFromPostingList', selectedWorkpackage._id);
        return;
      }
      // if no error and no 204 then we succeeded
      commit('removeWorkpackageFromPostingList', selectedWorkpackage._id);
      dispatch('addPostWorkpackageNotification', {
        selectedWorkpackage,
        jobStatus: jobStatuses.finished,
      });
    },

    async downloadExportToSAP(
      { commit, dispatch },
      { selectedWorkpackage, fieldConfigs, exportConfig, exportType, toFTP }
    ) {
      if (!selectedWorkpackage) {
        return Promise.resolve([]);
      }
      dispatch('addExportWorkpackageNotification', {
        selectedWorkpackage,
        jobStatus: jobStatuses.starting,
      });
      commit('addWorkpackageToExportingToSAPList', selectedWorkpackage._id);
      const params = {
        export: true,
        fieldConfigs,
        parseConfig: {
          header: exportConfig.csvHeader,
          delimiter: exportConfig.csvDelimiter,
          quote: exportConfig.csvQuote,
        },
        exportType,
        toFTP,
      };
      const url = `/api/work-packages/workpackage/${selectedWorkpackage._id}/results`;
      const [err, response] = await to(
        toFTP ? axios.post(url, {}, { params }) : axios.get(url, { params })
      );
      if (err) {
        dispatch('addExportWorkpackageNotification', { selectedWorkpackage, jobStatus: 'failed' });
        commit('removeWorkpackageFromExportingToSAPList', selectedWorkpackage._id);
        throw new Error(err.message);
      }
      // response.data may be only header row. 2 or more rows mean at least one data row in export.
      if (isEmpty(response.data) || `${response.data}`.split('\n').length < 2) {
        dispatch('addExportWorkpackageNotification', {
          selectedWorkpackage,
          jobStatus: 'no_results',
        });
        commit('removeWorkpackageFromExportingToSAPList', selectedWorkpackage._id);
        return;
      }
      if (!toFTP) {
        const fileName = getExportFileName(exportConfig);
        downloadCsvUtils.downloadCsvFile(response.data, fileName);
      }
      commit('removeWorkpackageFromExportingToSAPList', selectedWorkpackage._id);
      dispatch('addExportWorkpackageNotification', {
        selectedWorkpackage,
        jobStatus: jobStatuses.finished,
      });
    },

    async downloadXSROutboundExport(
      { commit, dispatch },
      { selectedWorkpackage, exportConfig, toFTP = false }
    ) {
      if (!selectedWorkpackage || !selectedWorkpackage.goLiveDate) {
        console.warn('XSR export: Invalid workpackage data', selectedWorkpackage);
        return Promise.resolve([]);
      }

      // Guard: Check if workpackage already live (client-side)
      if (moment(selectedWorkpackage.goLiveDate).isSameOrBefore(moment(), 'day')) {
        dispatch('addExportWorkpackageNotification', {
          selectedWorkpackage,
          jobStatus: 'already_live',
        });
        return;
      }

      dispatch('addExportWorkpackageNotification', {
        selectedWorkpackage,
        jobStatus: jobStatuses.starting,
      });

      commit('addWorkpackageToExportingToSAPList', selectedWorkpackage._id);

      const params = {
        export: true,
        fields: JSON.stringify(XSR_COLUMNS.reduce((acc, col) => ({ ...acc, [col]: col }), {})), // XSR field order must match CSV spec
        parseConfig: {
          header: exportConfig.csvHeader,
          delimiter: exportConfig.csvDelimiter,
          quote: exportConfig.csvQuote,
        },
        exportType: exportToSAP,
        toFTP,
      };

      const [err, response] = await to(
        toFTP
          ? axios.post(
              `/api/work-packages/workpackage/${selectedWorkpackage._id}/results`,
              {},
              { params }
            )
          : axios.get(`/api/work-packages/workpackage/${selectedWorkpackage._id}/results`, {
              params,
            })
      );

      if (err || isEmpty(response.data)) {
        dispatch('addExportWorkpackageNotification', {
          selectedWorkpackage,
          jobStatus: err ? 'failed' : 'no_results',
        });
        commit('removeWorkpackageFromExportingToSAPList', selectedWorkpackage._id);
        return;
      }

      if (!toFTP) {
        const fileName = getExportFileName(exportConfig);
        downloadCsvUtils.downloadCsvFile(response.data, fileName);
      }

      commit('removeWorkpackageFromExportingToSAPList', selectedWorkpackage._id);
      dispatch('addExportWorkpackageNotification', {
        selectedWorkpackage,
        jobStatus: jobStatuses.finished,
      });
    },

    resetState({ commit }) {
      commit('resetWorkpackageSpecificState');
    },

    async setUserSelectionWorkpackage(
      { commit, dispatch, state, rootState },
      { selectedWorkpackageId = {} } = {}
    ) {
      let selectedWorkpackage = find(state.workpackages, wp => wp._id === selectedWorkpackageId);
      await dispatch('resetWorkpackageState', null, { root: true });
      if (!selectedWorkpackage) {
        // unlikely to happen, but if it does, fetch the workpackage data
        try {
          await dispatch('fetchWorkpackageData', { id: selectedWorkpackageId });
          selectedWorkpackage = find(state.workpackages, wp => wp._id === selectedWorkpackageId);
        } catch (error) {
          throw new Error('Invalid workpackage ID');
        }
      }
      commit('setSelectedWorkpackage', selectedWorkpackage);
      // re-fetch attribute metadata after attributes state being reset
      await Promise.all([
        dispatch('storeGroupRelationships/fetchStoreGroupRelationships', null, {
          root: true,
        }),
        dispatch('storeGroupRelationships/fetchWorkpackageStoreGroups', null, {
          root: true,
        }),
        dispatch('storeGroupRelationships/fetchStoreGroups', null, {
          root: true,
        }),
        dispatch('storeGroupRelationships/fetchStoregroupKeyNameMap', null, {
          root: true,
        }),
        dispatch('storeGroupRelationships/fetchStoreGroupChains', null, {
          root: true,
        }),
      ]);
      await dispatch('attributes/fetchAttributeMetadata', null, { root: true });

      let defaultFilterSet = false;
      // Add default filter as soon as metadata is loaded (if feature is supported)
      if (rootState.clientConfig.toggleLogic.useDefaultFilter) {
        defaultFilterSet = true;
        await dispatch('filters/setDefaultFilter', null, { root: true });
      }

      const defaultCategoryFilter = rootState.clientConfig.toggleLogic.defaultCategoryFilter;
      if (defaultCategoryFilter && get(defaultCategoryFilter, '0.attributeValue')) {
        defaultFilterSet = true;
        await dispatch(
          'filters/setDefaultCategoryFilter',
          { defaultCategoryFilter },
          { root: true }
        );
      }

      if (!defaultFilterSet) {
        // If feature is not supported - reset filters
        await dispatch(
          'filters/setSelectedFilter',
          {
            filterName: 'retailAttributesFilter',
            filterValue: [],
          },
          { root: true }
        );
      }

      dispatch('getToolStoreGroups');
      // dispatch actions which have been called at created() in home.vue
      dispatch('competitorMetadata/fetchCompetitorMetadata', null, { root: true });
      dispatch(
        'hierarchy/fetchHierarchyForWorkpackage',
        {
          params: {
            sortBy: 'levelEntryDescription',
            sortDirection: 1,
            where: {
              workpackageId: selectedWorkpackageId,
              wholesale: false,
            },
          },
        },
        { root: true }
      );

      // reset selected unit
      commit('setSelectedUnitLevelEntryKey', null);
    },

    async setUserSelectionUnit(
      { dispatch, commit, rootGetters },
      { selectedWorkpackageId, unitLevelEntryKey, parentLevelEntryKey } = {}
    ) {
      await dispatch('setUserSelectionWorkpackage', { selectedWorkpackageId });
      await dispatch(
        'gridView/setExpandedHierarchyLevelItems',
        {
          newExpandedHierarchy: [
            parentLevelEntryKey,
            parentLevelEntryKey,
            unitLevelEntryKey,
            null,
            null,
          ],
        },
        { root: true }
      );

      commit('setSelectedUnitLevelEntryKey', unitLevelEntryKey);

      // for unit manager view
      if (rootGetters['context/isUnitManager']) {
        dispatch(
          'filters/setSelectedFilter',
          { filterName: 'selectedLevelEntryKey', filterValue: unitLevelEntryKey },
          { root: true }
        );
      }
    },

    async getPriceChangeData({ commit }, { params = {} } = {}) {
      // use post instead of get, as the workpackageId list in the params can be very long and hit GET request length restriction
      const [err, response] = await to(
        axios.post(`/api/work-packages/priceChangesByWorkpackage`, params)
      );
      if (err) {
        throw new Error(err.message);
      }
      const numberOfPriceChangesArray = response.data;
      const numberOfPriceChangesByWpIdAndCategoryId = numberOfPriceChangesArray.reduce(
        (acc, priceObject) => {
          const key = `${priceObject._id.workpackageId}::${priceObject._id.categoryId}`;
          acc[key] = priceObject.numPriceChanges;
          return acc;
        },
        {}
      );
      commit('setNumberOfPriceChangesByWpIdAndCategoryId', numberOfPriceChangesByWpIdAndCategoryId);
    },

    async getToolStoreGroups({ state, commit, rootState }) {
      const workpackageId = state.selectedWorkpackage._id;
      if (!workpackageId) return;
      commit('setLoadingSelectedWorkpackageToolStoreGroups', true);
      const [err, res] = await to(
        axios.get(`/api/workpackage-store-groups/workpackage/${workpackageId}/list`)
      );
      if (err) {
        commit('setSelectedWorkpackageToolStoreGroups', []);
        commit('setLoadingSelectedWorkpackageToolStoreGroups', false);
        throw new Error(err.message);
      }
      const mappedData = res.data
        ? res.data.map(pricingGroup => ({
            key: pricingGroup.toolStoreGroupKey,
            description: pricingGroup.toolStoreGroupDescription,
            pricingStoreGroupKey: pricingGroup.pricingStoreGroupKey,
            lockedPrices: pricingGroup.lockedPrices,
            lockedPricesDate: pricingGroup.lockedPricesDate,
          }))
        : [];
      const sortedToolStoreGroups = sortStoreGroups(
        mappedData,
        rootState.clientConfig.storeGroupOrderConfig,
        'description'
      );
      commit('setSelectedWorkpackageToolStoreGroups', sortedToolStoreGroups);
      commit('setLoadingSelectedWorkpackageToolStoreGroups', false);
    },

    // Checks if workpackageId exists. If not in store but present in route, fetch it and save store.
    // Route workpackageId has priority over store. Select globally if new one from route.
    async checkForWorkpackageExistence({ state, getters, commit, dispatch }) {
      const storeWorkpackageId = get(state, ['selectedWorkpackage', '_id'], null);
      const { workpackageId: routeWorkpackageId } = getWorkPackageIdFromRoute();
      if (!storeWorkpackageId && !routeWorkpackageId) {
        return { absentInStore: true };
      }
      let workpackageId = storeWorkpackageId;
      if (routeWorkpackageId && storeWorkpackageId !== routeWorkpackageId) {
        workpackageId = routeWorkpackageId;
      }
      const [err, response] = await to(axios.get(`/api/work-packages/${workpackageId}/`));
      if (err) {
        console.error('checkForWorkpackageExistence error', err);
        return { exists: false };
      }
      commit('setWorkpackages', [...state.workpackages, response.data]);
      if (!getters.hasSelectedWorkpackage || storeWorkpackageId !== routeWorkpackageId) {
        // don't await here, because it's supposed to be quick check
        // we ensure workpackage selected in routers
        dispatch('setUserSelectionWorkpackage', { selectedWorkpackageId: workpackageId });
      }

      return { exists: true };
    },

    async fetchAOExtracts({ commit }) {
      const [
        err,
        {
          data: { aoExtracts, periodIds },
        },
      ] = await to(axios.get('/api/work-packages/workpackage/aoExtracts'));
      if (err) throw new Error(err.message);
      commit('setAOExtracts', aoExtracts);
      commit('setPeriodIds', periodIds);
    },

    async runCompetitorPriceSimulation({ commit, state }, { workpackageId }) {
      try {
        const { data } = await axios.post(
          `/api/competitor-metadata/workpackage/${workpackageId}/runCompetitorPriceSimulation`
        );

        // Get workpackage description for notification
        const workpackage = find(state.workpackages, { _id: workpackageId });
        const workpackageDescription = get(workpackage, 'description', '');

        // Show success notification
        commit(
          'notifications/addNotification',
          {
            id: `competitor_price_simulation-${Date.now()}`,
            jobStatus: 'finished',
            baseTranslation: 'notifications.competitor_price_simulation',
            notificationPayload: `${get(
              data,
              'total_update_count',
              0
            )} products updated in ${workpackageDescription}`,
          },
          { root: true }
        );

        return data;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error running competitor price simulation:', error);

        // Get workpackage description for error notification
        const workpackage = find(state.workpackages, { _id: workpackageId });
        const workpackageDescription = get(workpackage, 'description', '');

        // Show error notification
        commit(
          'notifications/addNotification',
          {
            id: `competitor_price_simulation_failed-${Date.now()}`,
            jobStatus: 'failed',
            baseTranslation: 'notifications.competitor_price_simulation',
            notificationPayload: workpackageDescription,
          },
          { root: true }
        );

        throw error;
      }
    },

    async rerunPriceValidations({ commit, dispatch, rootState }, { workpackageId } = {}) {
      commit('setRerunningPriceValidations', true);
      try {
        await axios.post(`/api/price-validations/workpackage/${workpackageId}/run`);
        // If a scenario is selected, refresh the validation data in the store so the grid reflects
        // updated results.
        if (
          rootState.filters.scenario &&
          rootState.clientConfig.priceValidations &&
          rootState.clientConfig.priceValidations.enabled
        ) {
          await dispatch(
            'priceValidations/fetchValidations',
            { workpackageId, scenarioKey: rootState.filters.scenario },
            { root: true }
          );
        }
      } catch (err) {
        console.error('Failed to rerun price validations', err);
      } finally {
        commit('setRerunningPriceValidations', false);
      }
    },
  },
};

export default store;
